import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import type {
  BackendApiPaths as paths,
  BackendApiComponents as components,
  QueryParams,
  RequestBody,
  ResponseBody,
  SavedEventsApiResponse,
  UserFollowingResponse,
  UserTicketsResponse,
  FollowedArtistsResponse,
  FollowedVenuesResponse,
  MarkSeenResponse,
  SponsoredEventMeta,
} from '@loci/types';
import type {
  UserPreferences,
  UserPrivacyPreferences,
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  MagicLinkRequest,
  MagicLinkRequestResponse,
  MagicLinkConsumeRequest,
  MagicLinkConsumeResponse,
  ResetPasswordRequest,
  ChangePasswordRequest,
  ChangeEmailRequest,
  UpdateProfileRequest,
  UpdateProfileResponse,
  VerifyEmailRequest,
  ResendVerificationRequest,
  ResendVerificationResponse,
  User,
  AuthResponse,
  PasskeyAuthenticationBeginResponse,
  PasskeyAuthenticationFinishRequest,
  PasskeyAuthenticationFinishResponse,
} from './types.js';
import {
  userPreferencesSchema,
  placeCardDetailsV1Schema,
  isValidPlaceId,
  PLACE_DETAILS_VERSION,
  type OAuthProviderName,
  type PlaceCardDetailsV1,
} from '@loci/validation';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { toLegacyNearbyEvents } from './compat.js';

/**
 * Body for the `updateUserPreferences` mutation (`PATCH /api/v1/preferences`).
 * The flat `Partial<UserPreferences>` the settings organisms send, plus an
 * optional resolved `location` (lat/lon) — e.g. from "Use current location" —
 * which the backend persists as the recommendation profile's home coordinates.
 * The display text rides on `UserPreferences.locationLabel`.
 *
 * `privacy` is an explicit, first-class part of this contract (FRE-1184): a
 * strict boolean allowlist (`profileVisible` / `showActivity` /
 * `locationEnabled` / `analyticsEnabled` / `trackBrowsing`) that the backend
 * merges per-key into `User.preferences.privacy` — a single-toggle save never
 * clobbers sibling privacy keys, and unknown keys are rejected with 400.
 * `notifications` is NOT accepted here; it has its own endpoint
 * (`PUT /api/v1/users/me/notification-preferences`).
 */
export type UpdateUserPreferencesArg = Omit<Partial<UserPreferences>, 'privacy'> & {
  location?: { lat: number; lon: number };
  privacy?: Partial<UserPrivacyPreferences>;
};

/** Response to `POST /api/v1/auth/passkey/register/begin`. */
export type PasskeyRegisterBeginResponse = PublicKeyCredentialCreationOptionsJSON;

/** Request body for `POST /api/v1/auth/passkey/register/finish`. */
export type PasskeyRegisterFinishRequest = RegistrationResponseJSON;

/** Response from `POST /api/v1/auth/passkey/register/finish`. */
export interface PasskeyRegisterFinishResponse {
  success: true;
  credentialId: string;
}

type LegacyEventsQueryParams = QueryParams<paths, '/events/nearby', 'get'> & {
  radiusKm?: number;
};

type LegacyEventsResponse = Omit<ResponseBody<paths, '/events/nearby', 'get', 200>, 'events'> & {
  events?: import('./compat.js').LegacyNearbyEvent[];
};

type CurrentUserResponse = ResponseBody<paths, '/api/v1/auth/me', 'get', 200>;

/** Response from `GET /api/v1/auth/sessions` — `{ sessions: AuthSession[] }`. */
export type AuthSessionsListApiResponse = ResponseBody<paths, '/api/v1/auth/sessions', 'get', 200>;
/** `session` on `GET /api/v1/auth/me` (sign-in method + migration eligibility: API-1808). */
export type AuthMeCurrentSession = components['schemas']['AuthMeCurrentSession'];
/** Response from `POST /api/v1/auth/magic-link/enable` (API-1808). */
export type EnableMagicLinkSignInResponse = ResponseBody<
  paths,
  '/api/v1/auth/magic-link/enable',
  'post',
  200
>;

/**
 * A single active (non-revoked, unexpired) session for the authenticated user,
 * as returned by `GET /api/v1/auth/sessions`. `isCurrent` marks the session
 * backing the caller's own access token.
 */
export type AuthSession = AuthSessionsListApiResponse['sessions'][number];

// FRE-801 onboarding completion endpoint — derive contract from the OpenAPI
// spec so this mutation can't drift from the backend (precedent: API-1436
// social-tab white-screen incident caused by hand-written types diverging
// from the canonical schema).
type CompleteOnboardingRequest = RequestBody<paths, '/api/v1/preferences/onboarding', 'post'>;
type CompleteOnboardingResponse = ResponseBody<
  paths,
  '/api/v1/preferences/onboarding',
  'post',
  200
>;

// API-1489 notification-preferences endpoints. Contract derived from the
// OpenAPI spec so these bindings can't drift from the backend. The PUT body is
// the strict allowlist of the three M1 toggles (savedEventReminders,
// newNearYou, priceDrops); unknown keys are rejected with 400 server-side.
export type NotificationPreferences = components['schemas']['NotificationPreferences'];
type NotificationPreferencesResponse = ResponseBody<
  paths,
  '/api/v1/users/me/notification-preferences',
  'get',
  200
>;
type UpdateNotificationPreferencesRequest = RequestBody<
  paths,
  '/api/v1/users/me/notification-preferences',
  'put'
>;

// WEB-438 web-push subscription endpoints (backend API-1490). Both routes are
// authed (cookie/JWT) and scoped server-side to `req.user.id` — the owning user
// is never read from the body. Contract derived from the OpenAPI spec so these
// bindings can't drift from the backend.
//
// `POST /api/v1/push/subscribe` body is the browser `PushSubscription.toJSON()`
// shape `{ endpoint, keys: { p256dh, auth }, expirationTime? }`; the DELETE body
// is just `{ endpoint }`.

/** `PushSubscription.toJSON()` payload sent to `POST /api/v1/push/subscribe`. */
export type WebPushSubscriptionRequest = RequestBody<paths, '/api/v1/push/subscribe', 'post'>;
/** Response from `POST /api/v1/push/subscribe` (201). */
export type WebPushSubscriptionResponse = ResponseBody<
  paths,
  '/api/v1/push/subscribe',
  'post',
  201
>;
/** Body for `DELETE /api/v1/push/subscribe` — the endpoint URL to remove. */
export type WebPushUnsubscribeRequest = RequestBody<paths, '/api/v1/push/subscribe', 'delete'>;
/** Response from `DELETE /api/v1/push/subscribe` (200). */
export type WebPushUnsubscribeResponse = ResponseBody<
  paths,
  '/api/v1/push/subscribe',
  'delete',
  200
>;

// WEB-432 notification INBOX endpoints (backend API-1488). Contract derived
// from the OpenAPI spec so these bindings can't drift from the backend. These
// are the single data path the bell, panel, and toasts derive from — the count
// always comes from `getUnreadCount`, never from a WS frame (see
// `apps/web/hooks/useUserNotifications.ts`).
//
// NOTE: the generated `paths` key for these routes is `/notifications*` (the
// spec declares them without the `/api/v1` prefix that the prefs route carries
// in its key), while the live route is mounted at `/api/v1/notifications*`.
// The `query` url below therefore targets `v1/notifications*` relative to the
// `${baseUrl}/api/` base — mirroring every other v1 endpoint in this file.

/** A single in-app notification row (`GET /api/v1/notifications` item). */
export type NotificationItem = components['schemas']['Notification'];
/** Cursor-paginated list response from `GET /api/v1/notifications`. */
export type NotificationListResponse = ResponseBody<paths, '/notifications', 'get', 200>;
/** Unread-count response from `GET /api/v1/notifications/unread-count`. */
export type NotificationUnreadCountResponse = ResponseBody<
  paths,
  '/notifications/unread-count',
  'get',
  200
>;
/** Request body for `POST /api/v1/notifications/mark-read`. */
export type MarkNotificationsReadRequest = RequestBody<paths, '/notifications/mark-read', 'post'>;
/** Response from `POST /api/v1/notifications/mark-read`. */
export type MarkNotificationsReadResponse = ResponseBody<
  paths,
  '/notifications/mark-read',
  'post',
  200
>;
/**
 * Cursor-pagination args for `getNotifications`, derived from the OpenAPI spec
 * so they can't drift from the backend (same pattern as
 * `ForYouRecommendationsQuery`). The generated query carries `cursor?: string`
 * (opaque cursor — the `id` of the last item from the prior page) and
 * `limit?: number` (page size; backend caps at 50, defaults to 20).
 */
export type GetNotificationsArg = NonNullable<QueryParams<paths, '/notifications', 'get'>>;

/** `code` the backend sends when resend-verification finds the account already verified (API-1811). */
export const EMAIL_ALREADY_VERIFIED_CODE = 'EMAIL_ALREADY_VERIFIED';

/** True for the `POST /auth/resend-verification` 400 that means "nothing to do". Matches `code`, never message text. */
export function isEmailAlreadyVerifiedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (status !== 400 || typeof data !== 'object' || data === null) return false;
  return (data as { code?: unknown }).code === EMAIL_ALREADY_VERIFIED_CODE;
}

function emailAlreadyVerifiedMessage(error: FetchBaseQueryError): string {
  const message = (error.data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : 'This email address has already been verified.';
}

/** Align OpenAPI `User` (optional fields) with client `User` (required booleans / name null). */
function mapCurrentUserResponse(raw: CurrentUserResponse): {
  user: User;
  identities: components['schemas']['IdentityPublic'][];
  session?: components['schemas']['AuthMeCurrentSession'];
} {
  return {
    user: mapUserFromGetMe(raw.user),
    identities: raw.identities,
    ...(raw.session !== undefined ? { session: raw.session } : {}),
  };
}

function mapUserFromGetMe(apiUser: components['schemas']['User']): User {
  const base: User = {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name ?? null,
    emailVerified: apiUser.emailVerified ?? false,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt,
  };
  if (apiUser.preferences !== undefined) {
    const parsed = userPreferencesSchema.safeParse(apiUser.preferences);
    if (parsed.success) {
      base.preferences = parsed.data as UserPreferences;
    }
    // FRE-1187 — profile `bio`/`location` are stored inside the `preferences`
    // JSON by `PUT /api/v1/auth/profile`, but `userPreferencesSchema` strips
    // unknown keys, so lift them onto the top-level `User` where the Profile
    // settings form and identity card read them.
    const rawPreferences = apiUser.preferences as Record<string, unknown>;
    if (typeof rawPreferences.bio === 'string') {
      base.bio = rawPreferences.bio;
    }
    if (typeof rawPreferences.location === 'string') {
      base.location = rawPreferences.location;
    }
  }
  if (apiUser.pushToken !== undefined) {
    base.pushToken = apiUser.pushToken;
  }
  // API-1526 / FRE-864 — Instagram-linked badge flag. Present only on
  // `GET /api/v1/auth/me`; carried through so the settings surface can render
  // the badge without a second request.
  if (apiUser.instagramLinked !== undefined) {
    base.instagramLinked = apiUser.instagramLinked;
  }
  return base;
}

interface TrendingQuery {
  lat?: number;
  lon?: number;
  radius?: number;
  timeframe?: '1h' | '6h' | '24h' | '7d' | '30d';
  limit?: number;
}

interface TrendingEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  endDate?: string;
  imageUrl?: string;
  venue_name: string;
  city: string;
  state?: string;
  views: number;
  clicks: number;
  bookmarks: number;
  shares: number;
  engagement_score: number;
  recent_activity_count: number;
}

interface TrendingResponse {
  events: TrendingEvent[];
  searchCenter?: { lat: number; lon: number };
  searchRadius?: number;
  timeframe: string;
  generatedAt: string;
  fromCache: boolean;
}

interface MapChatViewport {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface MapChatRequest {
  prompt: string;
  viewport?: MapChatViewport;
}

export interface MapChatPlaceResult {
  placeId: string;
  name?: string;
}

export interface MapChatResponse {
  summary: string;
  places?: MapChatPlaceResult[];
  events: [];
}

/**
 * Shape returned by `GET /v1/auth/account/export` (FRE-810, HG-10).
 * Mirrors the backend payload — sensitive material (password hashes,
 * raw tokens, OAuth provider subjects) is already stripped on the server.
 */
export interface UserAccountExport {
  exportedAt: string;
  schemaVersion: number;
  user: {
    id: string;
    email: string;
    name: string | null;
    emailVerified: boolean;
    role: string;
    createdAt: string;
    updatedAt: string;
    accountDeletionRequestedAt: string | null;
    preferences: Record<string, unknown> | null;
  };
  identities: Array<{
    id: string;
    type: string;
    emailVerified: boolean;
    metadata: Record<string, unknown> | null;
    createdAt: string;
    updatedAt?: string;
  }>;
  savedEvents: Array<{
    id: string;
    eventId: string;
    savedAt: string;
    event: {
      id: string;
      title: string;
      startTime: string | null;
      endTime: string | null;
      source: string | null;
    } | null;
  }>;
}

type SearchSuggestionsResponse = ResponseBody<paths, '/events/search/suggestions', 'get', 200>;
/** Args for `getSearchSuggestions`: the partial query and the lowercase city slug (API-1821). */
type SearchSuggestionsArgs = QueryParams<paths, '/events/search/suggestions', 'get'>;

export interface EventsListQuery {
  categories?: string[];
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export type EventsListResponse = ResponseBody<paths, '/events/search', 'get', 200>;

// Recommendation query/response types — sourced from the generated OpenAPI
// operations in `@loci/types` so callers inherit schema drift automatically.
export type ForYouRecommendationsQuery = QueryParams<
  paths,
  '/api/v1/recommendations/for-you',
  'get'
>;
export type ForYouRecommendationsResponse = ResponseBody<
  paths,
  '/api/v1/recommendations/for-you',
  'get',
  200
>;

export type HappeningNowRecommendationsQuery = QueryParams<
  paths,
  '/api/v1/recommendations/happening-now',
  'get'
>;

/** Raw OpenAPI-generated shape, before the WEB-36 `sponsored` widening below. */
type HappeningNowRecommendationsResponseBase = ResponseBody<
  paths,
  '/api/v1/recommendations/happening-now',
  'get',
  200
>;

/**
 * WEB-36 — `/recommendations/happening-now` already emits an optional
 * `sponsored` annotation per event (API-1361's `injectSponsoredSlots`), but
 * that field isn't documented in `specs/openapi/backend-api-v1.yaml` yet, so
 * the generated `paths` type doesn't carry it. Widen the generated response
 * locally (an intersection, not an edit to the generated file, which
 * `pnpm api:generate-types` would overwrite) so callers get the field typed
 * without a spec change. Delete this widening once the OpenAPI spec catches
 * up and regenerates it for real.
 */
export type HappeningNowRecommendationsResponse = Omit<
  HappeningNowRecommendationsResponseBase,
  'events'
> & {
  events: Array<
    HappeningNowRecommendationsResponseBase['events'][number] & {
      sponsored?: SponsoredEventMeta;
    }
  >;
};

export type NewArtistRecommendationsQuery = QueryParams<
  paths,
  '/api/v1/recommendations/new-artists',
  'get'
>;
export type NewArtistRecommendationsResponse = ResponseBody<
  paths,
  '/api/v1/recommendations/new-artists',
  'get',
  200
>;

export type SimilarEventsQuery = QueryParams<
  paths,
  '/api/v1/recommendations/similar/{eventId}',
  'get'
>;
export type SimilarEventsResponse = ResponseBody<
  paths,
  '/api/v1/recommendations/similar/{eventId}',
  'get',
  200
>;

export type RecommendationFeedbackRequest = RequestBody<
  paths,
  '/api/v1/recommendations/feedback',
  'post'
>;
export type RecommendationFeedbackResponse = ResponseBody<
  paths,
  '/api/v1/recommendations/feedback',
  'post',
  202
>;

// Default values for events list query
const DEFAULT_EVENTS_LIST_PAGE = 1;
const DEFAULT_EVENTS_LIST_LIMIT = 20;

// Default values for trending events query
const DEFAULT_TRENDING_RADIUS = 25;
const DEFAULT_TRENDING_TIMEFRAME = '24h' as const;
const DEFAULT_TRENDING_LIMIT = 6;

// Get the API base URL from environment variables
const getApiBaseUrl = (() => {
  let cached: string | undefined;
  return () => {
    if (cached) return cached;

    const raw =
      process.env.NEXT_PUBLIC_API_URL ??
      process.env.API_BASE_URL ??
      process.env.EXPO_PUBLIC_API_BASE_URL;

    if (!raw) {
      const isTest =
        process.env.NODE_ENV === 'test' || typeof process.env.JEST_WORKER_ID !== 'undefined';
      if (isTest) {
        cached = '/';
        return cached;
      }
      // Fallback gracefully — throwing here kills every serverless function on cold start
      // because this runs at module-load time via fetchBaseQuery({ baseUrl: ... }).
      // Vercel sets NODE_ENV=production for ALL deployments (including preview), so a throw
      // here would crash previews if the env var is transiently missing.
      cached = 'https://api.freqseek.io';
      return cached;
    }

    if (!/^https?:\/\//i.test(raw) && raw !== '/') {
      throw new Error(
        `Invalid API base URL value: "${raw}". Must start with http:// or https:// or be "/".`,
      );
    }

    cached = raw.replace(/\/+$/, '');
    return cached;
  };
})();

const FREQ_SEEK_TAG_TYPES = [
  'Event',
  'User',
  'Category',
  'Location',
  'Recommendation',
  'Trending',
  'SavedEvents',
  'Place',
  'ArtistFollow',
  // FRE-925 — explicit venue follow graph. Mirrors `ArtistFollow`: keyed by
  // venue id for per-entity follow state and by `'LIST'` for the followed-venues
  // rail. The follow mutations + mark-seen invalidate these to refresh the rail.
  'VenueFollow',
  'EventNotes',
  'UserFollow',
  'Ticket',
  // WEB-53 — `/discover` events + facets payload. Future POST /events/feedback
  // (M7.x) invalidates this tag to refresh ranking-driven facet counts.
  'DiscoverEvents',
  // API-1489 / WEB-437 — allowlisted notification toggles. PUT invalidates this
  // tag so the GET re-fetches the persisted snapshot.
  'NotificationPreferences',
  // WEB-432 — notification INBOX (list + unread count). `markRead` invalidates
  // this tag; the `userNotifications` WS frame also invalidates it (the frame
  // is a "something changed" hint that triggers a refetch — it NEVER mutates a
  // client-side count). See `apps/web/hooks/useUserNotifications.ts`.
  'Notifications',
  // WEB-443 — Apple Music connection state. `getMusicStatus` provides this tag;
  // `connectMusic` (and a future disconnect) invalidates it so the connected
  // indicator refreshes after a connect. See `endpoints/music.ts`.
  'MusicConnection',
  // Browsing-history row count backing the "Clear browsing history" privacy
  // dialog. `getBrowsingHistorySummary` provides it; `clearBrowsingHistory`
  // invalidates it so a re-opened dialog reflects the post-clear count.
  'BrowsingHistory',
  // FRE-1147 — Discover saved-search CRUD (FRE-926 backend). `listSavedSearches`
  // provides `{ type: 'SavedSearch', id: 'LIST' }`; create/delete invalidate it.
  // Deliberately never invalidates `DiscoverEvents` — the saved-searches rail
  // reads its list off the discover envelope, which the mutations patch
  // optimistically instead (see `endpoints/savedSearches.ts`), preserving the
  // single-fetch cold-load invariant.
  'SavedSearch',
  // API-1630 — advertiser self-serve portal reads. `listMyVenueClaims` provides
  // `{ type: 'VenueClaim', id: 'LIST' }` and `listMySponsoredListings` provides
  // `{ type: 'SponsoredListing', id: 'LIST' }`; the matching create mutations
  // invalidate them so the portal status inbox reflects a submission without a
  // reload. Checkout-session create invalidates `SponsoredListing` too — the
  // listing's Stripe state changes as soon as the advertiser pays.
  'VenueClaim',
  'SponsoredListing',
] as const;

/**
 * Places responses are served by the Next.js Route Handler at
 * `apps/web/app/api/places/[placeId]`, which lives on the web origin
 * rather than behind the backend-api URL that `fetchBaseQuery` targets. A
 * relative path keeps this as a same-origin fetch from the browser.
 */
const PLACES_PROXY_PATH = '/api/places';

/**
 * RTK Query TTL strategy (FRE-666 — `keepUnusedDataFor` tuning):
 *
 * RTK Query keeps query data in the cache for `keepUnusedDataFor` seconds
 * after the last subscriber unmounts. Tuning these per-endpoint prevents
 * unbounded memory growth across repeated search cycles while keeping the
 * data fresh enough for the surface that consumes it.
 *
 * | Endpoint                     | TTL          | Rationale                                                                          |
 * | ---------------------------- | ------------ | ---------------------------------------------------------------------------------- |
 * | `getPlaceDetails`            | 2 min        | Business hours flip throughout the day; 2 min keeps "Open now" honest.             |
 * | Place photo bytes (HTTP)     | 24 h         | Served from `/api/places/photos` with `Cache-Control: private, max-age=86400`.     |
 * | `mapChat` / Gemini stream    | n/a (mutation) | Streamed answers are one-shot; we never replay a stale stream.                    |
 * | `getEvents` / `getEventsList`| RTK default (60 s) | Marker set turns over with viewport pan; default keeps memory bounded.       |
 * | `getTrendingEvents`          | RTK default  | Tag-invalidated on save/unsave; default TTL is fine for short browsing sessions.    |
 * | `get*Recommendations`        | RTK default  | Re-fetched on auth or feedback events via tag invalidation.                        |
 * | `getCurrentUser`             | RTK default  | Invalidated by every auth mutation; cache is short-lived in practice.              |
 *
 * On explicit search-clear (filter sheet reset, mobile drawer dismiss) the
 * UI dispatches `lociApi.util.resetApiState()` + `clearMap()` to drop
 * stale entries immediately rather than waiting for `keepUnusedDataFor` to
 * expire — see `SearchFiltersSheet.handleReset` and `MobileSearchBar`.
 */
const PLACE_DETAILS_KEEP_UNUSED_SECONDS = 2 * 60;

export type { PlaceCardDetailsV1 } from '@loci/validation';

/**
 * Arguments for the `getSavedEvents` query.
 *
 * @property userId - Identifier of the user whose saved events should be fetched.
 * @property limit  - Optional page size for the returned list.
 * @property offset - Optional zero-based offset for pagination.
 */
/** Response from `GET /api/v1/events/:id/user-state` (authenticated). */
export interface EventUserStateResponse {
  isSaved: boolean;
  calendarAdded: boolean;
  canAskLoci: boolean;
}

/** Response from `GET /api/v1/events/:id/social` — see the OpenAPI `EventSocialResponse` schema. */
export type EventSocialResponse = components['schemas']['EventSocialResponse'];
/** A single friend/mutual card within {@link EventSocialResponse.friendsGoing}. */
export type EventSocialFriend = components['schemas']['EventSocialFriend'];

export interface GetSavedEventsArg {
  userId: string;
  limit?: number;
  offset?: number;
  /**
   * Opt-in flag (FRE-770). When true, the response includes per-location
   * `latitude`/`longitude` and a server-computed `routeDistanceMeters` total.
   * Cache key includes this flag so coordinate-aware and lean responses don't
   * collide.
   */
  withCoordinates?: boolean;
}

/** Arguments for the `getUserFootprint` query (WEB-477). */
export interface GetUserFootprintArg {
  /** Identifier of the user whose saved-venue footprint should be counted. */
  userId: string;
}

/**
 * Response from `GET /api/v1/analytics/user/:userId/footprint` (WEB-477).
 *
 * `savedVenueCount` is the number of DISTINCT venues among the member's saved
 * events — the real "N places in your footprint" metric. `0` when the user has
 * saved nothing (the footer omits the footprint line in that case rather than
 * fabricating a count). Derived from the OpenAPI spec so it can never drift
 * from the backend contract.
 */
export type UserFootprintResponse = ResponseBody<
  paths,
  '/api/analytics/user/{userId}/footprint',
  'get',
  200
>;

/**
 * Arguments for the `saveEvent` mutation.
 *
 * @property userId    - Identifier of the user saving the event.
 * @property eventId   - Identifier of the event being saved.
 * @property sessionId - Optional analytics session id for attribution.
 */
export interface SaveEventArg {
  userId: string;
  eventId: string;
  sessionId?: string;
}

/**
 * Arguments for the `unsaveEvent` mutation.
 *
 * Cancellation is handled through RTK Query's built-in `.abort()` on the
 * trigger promise returned by `useUnsaveEventMutation` — consumers should
 * capture that promise and call `abort()` instead of passing a custom signal.
 *
 * @property userId    - Identifier of the user unsaving the event.
 * @property eventId   - Identifier of the event being unsaved.
 * @property sessionId - Optional analytics session id for attribution.
 */
export interface UnsaveEventArg {
  userId: string;
  eventId: string;
  sessionId?: string;
}

/**
 * Result of an RSVP create/delete on an event (API-1485, WEB-412). `going`
 * reflects the user's post-operation state; `goingCount` is the live count of
 * all RSVPs for the event. Generated OpenAPI schema is the single source of
 * truth.
 */
export type EventRsvpResponse = components['schemas']['EventRsvpResponse'];

/**
 * Arguments for the `rsvpEvent` / `unrsvpEvent` mutations (WEB-412).
 *
 * The backend identifies the user from the authenticated session cookie, so
 * only the event identifier is required. Cancellation is handled through RTK
 * Query's built-in `.abort()` on the trigger promise.
 *
 * @property eventId - Identifier (or slug-resolvable id) of the event.
 */
export interface RsvpEventArg {
  eventId: string;
}

export type LociTagType = (typeof FREQ_SEEK_TAG_TYPES)[number];

/**
 * Whether a tag's cached data depends on WHO is signed in. The store outlives
 * a principal change (login/logout/reset navigate client-side), and cache keys
 * never include the user, so every `principal` tag must be invalidated when the
 * principal changes or one account's data is served to the next (WEB-923,
 * CWE-200). `satisfies Record<LociTagType, …>` makes classification exhaustive:
 * a new tag type fails `type-check` until it is placed here.
 */
const TAG_SCOPE = {
  Event: 'principal', // per-user saved/RSVP state rides on event payloads
  User: 'principal',
  Category: 'public',
  Location: 'public',
  Recommendation: 'principal',
  Trending: 'public',
  SavedEvents: 'principal',
  Place: 'public',
  ArtistFollow: 'principal',
  VenueFollow: 'principal',
  EventNotes: 'principal',
  UserFollow: 'principal',
  Ticket: 'principal',
  DiscoverEvents: 'principal', // personalized ranking + saved-search rail
  NotificationPreferences: 'principal',
  Notifications: 'principal',
  MusicConnection: 'principal',
  BrowsingHistory: 'principal',
  SavedSearch: 'principal',
  VenueClaim: 'principal',
  SponsoredListing: 'principal',
} as const satisfies Record<LociTagType, 'principal' | 'public'>;

/**
 * Invalidated (whole type) by every mutation that can change the signed-in
 * principal: login, register, magic-link consume, passkey finish, password
 * reset, logout, logout-all and account deletion.
 */
const PRINCIPAL_SCOPED_TAG_TYPES = (Object.keys(TAG_SCOPE) as LociTagType[]).filter(
  (tag) => TAG_SCOPE[tag] === 'principal',
);

/**
 * For mutations that change the principal only when they SUCCEED (sign-in,
 * sign-up, magic link, passkey, password reset). A static array would also
 * fire on a rejected attempt, so a mistyped password would refetch every
 * principal-scoped query for an unchanged principal. Logout-type mutations
 * keep the static list: a failed logout can still leave the session cleared.
 */
const invalidatePrincipalOnSuccess = (_result: unknown, error: unknown): readonly LociTagType[] =>
  error ? [] : PRINCIPAL_SCOPED_TAG_TYPES;

/**
 * Artist follow state payload returned by
 * `GET /api/v1/artists/:id/follow` and both follow mutations. Derived from
 * the OpenAPI contract (`ArtistFollowState` schema) so spec drift surfaces
 * here at type-check time.
 */
export type ArtistFollowState = ResponseBody<paths, '/api/v1/artists/{id}/follow', 'get', 200>;

/**
 * Venue follow state payload returned by
 * `GET /api/v1/venues/:id/follow` and both venue follow mutations (FRE-925).
 */
export interface VenueFollowState {
  venueId: string;
  following: boolean;
}

/**
 * Single community note rendered in the Event Detail COMMUNITY NOTES card.
 */
export interface EventNote {
  id: string;
  content: string;
  authorHandle: string;
  createdAt: string;
}

/**
 * Response shape for `GET /api/v1/events/:id/notes`.
 */
export interface EventNotesResponse {
  notes: EventNote[];
}

/**
 * Patch every in-flight `getSavedEvents` cache entry for the given user.
 *
 * `getSavedEvents` is keyed by the full arg object (userId, limit, offset),
 * so a save/unsave optimistic update needs to touch every cached variant the
 * UI is currently subscribed to — otherwise an uncommon `{ limit: 20 }` call
 * would lag behind the canonical `{ limit: 100 }` one. We look up all active
 * `SavedEvents` subscriptions via `selectInvalidatedBy`, filter them back to
 * the right userId via the original query arg, and return the undo handles
 * so the caller can roll back on failure.
 */
/**
 * Store slice shape that `selectInvalidatedBy` needs to read from.
 * `selectInvalidatedBy` only reads `state[reducerPath]` internally, so any
 * state that contains the RTK Query slice at this key is a valid argument.
 */
type LociRootState = {
  [lociApi.reducerPath]: ReturnType<typeof lociApi.reducer>;
};

/** Dispatch shape supplied by RTK Query's `onQueryStarted` hooks. */
type SavedEventsDispatch = ThunkDispatch<LociRootState, unknown, UnknownAction>;

function patchSavedEventsForUser(
  dispatch: SavedEventsDispatch,
  userId: string,
  recipe: (draft: SavedEventsApiResponse, args: GetSavedEventsArg) => void,
): Array<{ undo: () => void }> {
  const patches: Array<{ undo: () => void }> = [];
  dispatch((_dispatch, getState: () => LociRootState) => {
    const invalidated = lociApi.util.selectInvalidatedBy(getState(), [
      { type: 'SavedEvents', id: 'LIST' },
    ]);
    for (const entry of invalidated) {
      if (entry.endpointName !== 'getSavedEvents') continue;
      // `originalArgs` carries the subscription's arg shape, which is our
      // GetSavedEventsArg. Skip entries for other users.
      const args = entry.originalArgs as GetSavedEventsArg | undefined;
      if (!args || args.userId !== userId) continue;
      const patch = dispatch(
        lociApi.util.updateQueryData('getSavedEvents', args, (draft) => recipe(draft, args)),
      );
      patches.push(patch);
    }
  });
  return patches;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${getApiBaseUrl()}/api/`,
  credentials: 'include',
  prepareHeaders: (headers, { type }) => {
    try {
      const correlationId =
        globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
      headers.set('x-correlation-id', correlationId);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to generate correlation ID:', error);
      }
    }

    // Attach CSRF token on mutations (POST/PUT/PATCH/DELETE).
    // Read from the _csrf cookie set by GET /api/v1/auth/csrf-token.
    if (type === 'mutation') {
      try {
        const csrfToken =
          typeof document !== 'undefined'
            ? (() => {
                const match = document.cookie.split('; ').find((c) => c.startsWith('_csrf='));
                if (!match) return undefined;
                const idx = match.indexOf('=');
                return idx >= 0 ? match.substring(idx + 1) : undefined;
              })()
            : undefined;
        if (csrfToken) {
          headers.set('x-csrf-token', csrfToken);
        }
      } catch {
        // SSR or cookie access unavailable — skip
      }
    }

    return headers;
  },
});

/**
 * Endpoints whose own 401 must NOT trigger a transparent reauth retry (WEB-490).
 *
 *   - `refreshToken` — reauthing in response to the refresh call's own 401 would
 *     recurse.
 *   - `login` / `register` / `logout` / `getCsrfToken` and the password / email /
 *     passkey auth-flow endpoints — a 401/403 here is the expected outcome (bad
 *     credentials, expired reset token, capability probe), not a stale access
 *     token, so a refresh can't help and would just burn the rate-limited
 *     refresh budget.
 *
 * `getCurrentUser` is deliberately NOT excluded: an `/auth/me` 401 caused by an
 * expired-but-refreshable access token is exactly the case auto-refresh exists
 * to recover — refresh once, retry, and the user resolves authenticated instead
 * of being torn down by the stale-session recovery.
 */
const NO_REAUTH_ENDPOINTS: ReadonlySet<string> = new Set([
  'refreshToken',
  'login',
  'register',
  'logout',
  'getCsrfToken',
  'forgotPassword',
  'resetPassword',
  'verifyEmail',
  'resendVerification',
  'requestMagicLink',
  'consumeMagicLink',
  'passkeyAuthenticationBegin',
  'passkeyAuthenticationFinish',
]);

/**
 * In-flight refresh promise shared across concurrent 401s.
 *
 * Without it, N parallel queries that all 401 on the same expired access token
 * would each fire their own `POST /auth/refresh` — a thundering herd against an
 * endpoint that is itself rate-limited (10 / 5 min). Dedupe onto one refresh,
 * mirroring the admin SPA's `apps/web/lib/admin/dataProvider.ts` reauth mutex.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function attemptTokenRefresh(
  api: Parameters<typeof rawBaseQuery>[1],
  extraOptions: Parameters<typeof rawBaseQuery>[2],
): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const result = await rawBaseQuery(
      { url: 'v1/auth/refresh', method: 'POST' },
      api,
      extraOptions,
    );
    return result.error == null;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Reauth-aware base query (WEB-490).
 *
 * Consumer access tokens are short-lived (5 min admin / 15 min user) while the
 * refresh token lives far longer (8 h / 30 d). Before this wrapper the consumer
 * app had no auto-refresh — the first authed request after access-token expiry
 * 401'd, the app concluded "logged out," and the user was bounced to /login even
 * though their session was still alive via the refresh cookie ("force-logged out
 * very quickly"). The admin SPA already solved this in `dataProvider.ts`; this
 * brings the same behavior to every consumer RTK Query call.
 *
 * On a 401 from a reauth-eligible endpoint (browser only — refresh is
 * cookie-based), attempt a single deduped refresh and, on success, retry the
 * original request exactly once. A 401 that survives a failed-or-successful
 * refresh falls through unchanged to `rtkQueryErrorMiddleware` →
 * `handleUnauthorized` → `sanitizeInvalidSession()`, so a genuinely dead session
 * still ends in a clean logout. A single retry means no reauth loop.
 */
const baseQueryWithReauth: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (
    result.error?.status !== 401 ||
    typeof window === 'undefined' ||
    NO_REAUTH_ENDPOINTS.has(api.endpoint)
  ) {
    return result;
  }

  const refreshed = await attemptTokenRefresh(api, extraOptions);
  if (!refreshed) return result;

  return rawBaseQuery(args, api, extraOptions);
};

export const lociApi = createApi({
  reducerPath: 'lociApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: FREQ_SEEK_TAG_TYPES,
  endpoints: (builder) => ({
    // Authentication queries
    getCurrentUser: builder.query<
      | {
          user: User;
          identities: components['schemas']['IdentityPublic'][];
          session?: components['schemas']['AuthMeCurrentSession'];
        }
      | { user: null },
      void
    >({
      // queryFn (not `query` + transformErrorResponse) so a 401 settles as
      // *fulfilled* `{ user: null }`. transformErrorResponse only rewrites the
      // error payload — the cache entry stays rejected, and a second subscriber
      // (CityChrome + AuthProvider + AppInitializer) re-issues the whole
      // me→refresh cycle (WEB-810). Fulfilled anonymous state is reused.
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const result = await baseQuery('v1/auth/me');
        if (result.error) {
          if (result.error.status === 401) {
            return { data: { user: null } };
          }
          return { error: result.error as FetchBaseQueryError };
        }
        return { data: mapCurrentUserResponse(result.data as CurrentUserResponse) };
      },
      providesTags: [{ type: 'User', id: 'SELF' }],
    }),

    listAuthSessions: builder.query<AuthSessionsListApiResponse, void>({
      query: () => 'v1/auth/sessions',
      providesTags: [{ type: 'User', id: 'SESSIONS' as const }],
    }),

    getCsrfToken: builder.query<{ csrfToken: string }, void>({
      query: () => 'v1/auth/csrf-token',
    }),

    // Authentication mutations
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: 'v1/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: invalidatePrincipalOnSuccess,
    }),

    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (userData) => ({
        url: 'v1/auth/register',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: invalidatePrincipalOnSuccess,
    }),

    logout: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: 'v1/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: PRINCIPAL_SCOPED_TAG_TYPES,
    }),

    /**
     * Soft-delete the current account (FRE-810, HG-10). Honored by the
     * backend immediately — cookies are cleared on the response and the
     * row is marked with a 30-day grace window. Logging back in within the
     * grace period restores the account.
     */
    deleteAccount: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: 'v1/auth/account',
        method: 'DELETE',
      }),
      invalidatesTags: PRINCIPAL_SCOPED_TAG_TYPES,
    }),

    /**
     * Download a JSON snapshot of the user's profile, preferences,
     * identities (sanitized), and saved events. Honors the Privacy
     * Policy's "Download my data" commitment (FRE-810, HG-10).
     */
    exportAccountData: builder.mutation<UserAccountExport, void>({
      query: () => ({
        url: 'v1/auth/account/export',
        method: 'GET',
      }),
    }),

    /**
     * Real count of the user's stored browsing-history rows, backing the
     * "Clear browsing history" confirmation dialog. Replaces a hardcoded
     * placeholder — the dialog states exactly what will be removed.
     */
    getBrowsingHistorySummary: builder.query<{ recordCount: number }, void>({
      query: () => ({
        url: 'v1/auth/account/browsing-history/summary',
        method: 'GET',
      }),
      providesTags: [{ type: 'BrowsingHistory', id: 'SUMMARY' }],
    }),

    /**
     * Delete all of the user's browsing history. Backs the "Clear browsing
     * history" privacy control — a real deletion. Invalidates the summary so a
     * re-opened dialog reflects the post-clear count.
     */
    clearBrowsingHistory: builder.mutation<{ deletedCount: number }, void>({
      query: () => ({
        url: 'v1/auth/account/browsing-history',
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'BrowsingHistory', id: 'SUMMARY' }, 'Recommendation'],
    }),

    /**
     * Clear the user's stored home location (nulls `homeLat`/`homeLon` on the
     * preference profile). Backs the "Clear stored location" privacy control —
     * a real write, not a no-op.
     */
    clearStoredLocation: builder.mutation<{ cleared: boolean; profilesUpdated: number }, void>({
      query: () => ({
        url: 'v1/auth/account/stored-location',
        method: 'DELETE',
      }),
      invalidatesTags: ['User', 'Recommendation'],
    }),

    logoutAll: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: 'v1/auth/logout-all',
        method: 'POST',
      }),
      invalidatesTags: PRINCIPAL_SCOPED_TAG_TYPES,
    }),

    revokeSession: builder.mutation<{ message: string }, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `v1/auth/sessions/${encodeURIComponent(sessionId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    refreshToken: builder.mutation<{ accessToken: string; message: string }, void>({
      query: () => ({
        url: 'v1/auth/refresh',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'User', id: 'SESSIONS' }],
    }),

    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (data) => ({
        url: 'v1/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),

    resetPassword: builder.mutation<
      { message: string; email: string; accessToken: string; refreshToken: string },
      ResetPasswordRequest
    >({
      query: (data) => ({
        url: 'v1/auth/reset-password',
        method: 'POST',
        body: data,
      }),
      // The reset token selects the account and a new session is issued for
      // it, so the principal may change.
      invalidatesTags: invalidatePrincipalOnSuccess,
    }),

    verifyEmail: builder.mutation<{ message: string; email: string }, VerifyEmailRequest>({
      query: (data) => ({
        url: 'v1/auth/verify-email',
        method: 'POST',
        body: data,
      }),
    }),

    // API-1811: an already-verified account is not a failure. The backend
    // answers 400 `EMAIL_ALREADY_VERIFIED`; resolve it as success so callers
    // show one non-error message and the global error middleware raises no
    // second toast, and flip the cached `/auth/me` user to verified.
    resendVerification: builder.mutation<ResendVerificationResponse, ResendVerificationRequest>({
      async queryFn(data, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: 'v1/auth/resend-verification',
          method: 'POST',
          body: data,
        });
        if (result.error) {
          if (isEmailAlreadyVerifiedError(result.error)) {
            return {
              data: { message: emailAlreadyVerifiedMessage(result.error), alreadyVerified: true },
            };
          }
          return { error: result.error as FetchBaseQueryError };
        }
        return { data: result.data as ResendVerificationResponse };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data.alreadyVerified) return;
          dispatch(
            lociApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
              if (draft.user) draft.user.emailVerified = true;
            }),
          );
        } catch {
          // A real failure is the caller's to surface.
        }
      },
    }),

    /**
     * Request a passwordless magic-link email (WEB-568).
     * `POST /api/v1/auth/magic-link/request` — always 200 (anti-enumeration).
     */
    requestMagicLink: builder.mutation<MagicLinkRequestResponse, MagicLinkRequest>({
      query: (data) => ({
        url: 'v1/auth/magic-link/request',
        method: 'POST',
        body: data,
      }),
    }),

    /**
     * Exchange a magic-link token for a session (WEB-568).
     * `POST /api/v1/auth/magic-link/consume` — sets httpOnly cookies + returns
     * tokens in the body (same contract as `/login`).
     */
    consumeMagicLink: builder.mutation<MagicLinkConsumeResponse, MagicLinkConsumeRequest>({
      query: (data) => ({
        url: 'v1/auth/magic-link/consume',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: invalidatePrincipalOnSuccess,
    }),

    changePassword: builder.mutation<{ message: string }, ChangePasswordRequest>({
      query: (data) => ({
        url: 'v1/auth/change-password',
        method: 'PUT',
        body: data,
      }),
    }),

    /**
     * Request an email change (API-1804). The session must hold a fresh
     * step-up (passkey if enrolled, otherwise a magic-link consume). The
     * server stores `pendingNewEmail` and emails a verification link to the
     * NEW address — the account email does NOT change until that link is
     * confirmed via `POST /auth/change-email/verify`, so there is no `User`
     * tag invalidation here (nothing user-visible changed yet).
     */
    changeEmail: builder.mutation<{ message: string }, ChangeEmailRequest>({
      query: (data) => ({
        url: 'v1/auth/change-email/request',
        method: 'POST',
        body: data,
      }),
    }),

    /**
     * Update the current user's profile (FRE-1187) — `PUT /api/v1/auth/profile`,
     * down-shaped to the shipped backend contract `{ name, bio, location }`.
     * Invalidates the current-user tag so `/auth/me` refetches and the settings
     * identity card / prefill reflect the save without a manual refresh.
     */
    updateProfile: builder.mutation<UpdateProfileResponse, UpdateProfileRequest>({
      query: (data) => ({
        url: 'v1/auth/profile',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    // Passkey (WebAuthn) authentication — FRE-690 (backend) + FRE-692 (frontend).
    // Begin returns `PublicKeyCredentialRequestOptionsJSON`; finish verifies the
    // browser's assertion and sets session cookies. `allowCredentials` is empty
    // by default to enable discoverable-credential (conditional UI) sign-in.
    passkeyAuthenticationBegin: builder.mutation<PasskeyAuthenticationBeginResponse, void>({
      query: () => ({
        url: 'v1/auth/passkey/authenticate/begin',
        method: 'POST',
        body: {},
      }),
    }),

    passkeyAuthenticationFinish: builder.mutation<
      PasskeyAuthenticationFinishResponse,
      PasskeyAuthenticationFinishRequest
    >({
      query: (assertion) => ({
        url: 'v1/auth/passkey/authenticate/finish',
        method: 'POST',
        body: assertion,
      }),
      invalidatesTags: invalidatePrincipalOnSuccess,
    }),

    /**
     * Nearby events — GET `/api/v1/events/nearby` on backend.
     * Query params follow OpenAPI: `categories` is a comma-separated list of category slugs
     * (e.g. `music`, `food`); `radius` is in km; pass `radiusKm` from the client for convenience.
     */
    getEvents: builder.query<LegacyEventsResponse, LegacyEventsQueryParams | void>({
      query: (params) => {
        const queryConfig: {
          url: string;
          params?: QueryParams<paths, '/events/nearby', 'get'>;
        } = {
          url: 'v1/events/nearby',
        };
        if (params !== undefined) {
          const { radiusKm, ...restParams } = params;
          const transformedParams: QueryParams<paths, '/events/nearby', 'get'> = { ...restParams };
          if (radiusKm !== undefined) {
            transformedParams.radius = radiusKm;
          }
          queryConfig.params = transformedParams;
        }
        return queryConfig;
      },
      transformResponse: (
        response: ResponseBody<paths, '/events/nearby', 'get', 200>,
      ): LegacyEventsResponse => {
        const events = Array.isArray(response.events)
          ? toLegacyNearbyEvents(response.events as components['schemas']['NearbyEvent'][])
          : [];
        return {
          ...response,
          events,
        };
      },
      providesTags: (result) =>
        result ? [{ type: 'Event', id: 'LIST' }] : [{ type: 'Event', id: 'LIST' }],
    }),

    mapChat: builder.mutation<MapChatResponse, MapChatRequest>({
      query: (body) => ({
        url: 'v1/maps/chat',
        method: 'POST',
        body,
      }),
    }),

    getTrendingEvents: builder.query<TrendingResponse, TrendingQuery>({
      query: (params) => ({
        url: 'v1/analytics/trending',
        params: {
          lat: params?.lat,
          lon: params?.lon,
          radius: params?.radius || DEFAULT_TRENDING_RADIUS,
          timeframe: params?.timeframe || DEFAULT_TRENDING_TIMEFRAME,
          limit: params?.limit || DEFAULT_TRENDING_LIMIT,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...(result.events || []).map(({ id }) => ({ type: 'Event' as const, id })),
              { type: 'Event', id: 'TRENDING' },
            ]
          : [{ type: 'Event', id: 'TRENDING' }],
    }),

    // API-1821 — suggestions are city-scoped; `city` is a required param.
    getSearchSuggestions: builder.query<SearchSuggestionsResponse, SearchSuggestionsArgs>({
      query: ({ q, city }) => ({
        url: 'v1/events/search/suggestions',
        params: { q, city },
      }),
    }),

    getEventsList: builder.query<EventsListResponse, EventsListQuery | void>({
      query: (params) => ({
        url: 'v1/events/search',
        params: {
          ...(params?.categories?.length ? { categories: params.categories.join(',') } : {}),
          dateFrom: params?.dateFrom,
          dateTo: params?.dateTo,
          page: params?.page ?? DEFAULT_EVENTS_LIST_PAGE,
          limit: params?.limit ?? DEFAULT_EVENTS_LIST_LIMIT,
        },
      }),
      providesTags: (result) =>
        result?.events
          ? [
              ...result.events.map(({ id }) => ({ type: 'Event' as const, id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),

    getCategories: builder.query<components['schemas']['Category'][], void>({
      query: () => 'v1/events/categories',
      transformResponse: (
        raw: ResponseBody<paths, '/events/categories', 'get', 200>,
      ): components['schemas']['Category'][] => {
        return raw.categories ?? [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Category' as const, id })),
              { type: 'Category', id: 'LIST' },
            ]
          : [{ type: 'Category', id: 'LIST' }],
    }),

    // Recommendations — API-1306. Typed end-to-end via `@loci/types`
    // generated operations so shapes track the OpenAPI spec automatically.
    getForYouRecommendations: builder.query<
      ForYouRecommendationsResponse,
      ForYouRecommendationsQuery
    >({
      query: (params) => ({
        url: 'v1/recommendations/for-you',
        params: params as Record<string, string | number | undefined>,
      }),
      providesTags: (result) =>
        result?.recommendations
          ? [
              ...result.recommendations.map(({ id }) => ({
                type: 'Recommendation' as const,
                id,
              })),
              { type: 'Recommendation', id: 'FOR_YOU' },
            ]
          : [{ type: 'Recommendation', id: 'FOR_YOU' }],
    }),

    getHappeningNowRecommendations: builder.query<
      HappeningNowRecommendationsResponse,
      HappeningNowRecommendationsQuery | void
    >({
      query: (params) => ({
        url: 'v1/recommendations/happening-now',
        params: (params ?? undefined) as Record<string, string | number | undefined> | undefined,
      }),
      providesTags: (result) =>
        result?.events
          ? [
              ...result.events.map(({ id }) => ({ type: 'Recommendation' as const, id })),
              { type: 'Recommendation', id: 'HAPPENING_NOW' },
            ]
          : [{ type: 'Recommendation', id: 'HAPPENING_NOW' }],
    }),

    getNewArtistRecommendations: builder.query<
      NewArtistRecommendationsResponse,
      NewArtistRecommendationsQuery
    >({
      query: (params) => ({
        url: 'v1/recommendations/new-artists',
        params: params as Record<string, string | number | undefined>,
      }),
      providesTags: (result) =>
        result?.events
          ? [
              ...result.events.map(({ id }) => ({ type: 'Recommendation' as const, id })),
              { type: 'Recommendation', id: 'NEW_ARTISTS' },
            ]
          : [{ type: 'Recommendation', id: 'NEW_ARTISTS' }],
    }),

    getSimilarEvents: builder.query<
      SimilarEventsResponse,
      { eventId: string; userId?: string; limit?: number }
    >({
      query: ({ eventId, userId, limit }) => ({
        url: `v1/recommendations/similar/${encodeURIComponent(eventId)}`,
        params: {
          ...(userId !== undefined ? { userId } : {}),
          ...(limit !== undefined ? { limit } : {}),
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: 'Recommendation', id: `SIMILAR_${eventId}` },
      ],
    }),

    recordRecommendationFeedback: builder.mutation<
      RecommendationFeedbackResponse,
      RecommendationFeedbackRequest
    >({
      query: (body) => ({
        url: 'v1/recommendations/feedback',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Recommendation', id: 'FOR_YOU' },
        { type: 'Recommendation', id: 'HAPPENING_NOW' },
        { type: 'Recommendation', id: 'NEW_ARTISTS' },
      ],
    }),

    // Provider unlink (API-1227). The optimistic patch removes the matching
    // OAuth identity from the `getCurrentUser` cache so the settings list
    // updates immediately; failures roll back and the `User/SELF` invalidation
    // reconciles with the server either way.
    unlinkProvider: builder.mutation<void, { provider: OAuthProviderName }>({
      query: ({ provider }) => ({
        url: `v1/auth/oauth/${provider}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ provider }, { dispatch, queryFulfilled }) {
        const targetType = `OAUTH_${provider.toUpperCase()}`;
        const patch = dispatch(
          lociApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            if (!('identities' in draft)) return;
            draft.identities = draft.identities.filter((identity) => identity.type !== targetType);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    // Passkey revoke (FRE-693). `credentialId` here is the `Identity.id` cuid —
    // matches `IdentityPublic.id` returned by `getCurrentUser`.
    unlinkPasskey: builder.mutation<void, { credentialId: string }>({
      query: ({ credentialId }) => ({
        url: `v1/auth/passkey/${encodeURIComponent(credentialId)}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ credentialId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          lociApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            if (!('identities' in draft)) return;
            draft.identities = draft.identities.filter((identity) => identity.id !== credentialId);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    // API-1808 — the passwordless migration banner's "Use email links from now
    // on". Idempotent server-side; refetches /auth/me so the banner sees the
    // new MAGIC_LINK identity and drops that option.
    enableMagicLinkSignIn: builder.mutation<EnableMagicLinkSignInResponse, void>({
      query: () => ({
        url: 'v1/auth/magic-link/enable',
        method: 'POST',
        body: {},
      }),
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    // Passkey registration (FRE-691) — authenticated user mints registration
    // options server-side, then posts the attestation back for verification +
    // persistence. The body shapes mirror @simplewebauthn/browser's
    // PublicKeyCredentialCreationOptionsJSON and RegistrationResponseJSON.
    passkeyRegisterBegin: builder.mutation<PasskeyRegisterBeginResponse, void>({
      query: () => ({
        url: 'v1/auth/passkey/register/begin',
        method: 'POST',
        body: {},
      }),
    }),

    passkeyRegisterFinish: builder.mutation<
      PasskeyRegisterFinishResponse,
      PasskeyRegisterFinishRequest
    >({
      query: (body) => ({
        url: 'v1/auth/passkey/register/finish',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    // Update user preferences. Backend route is mounted at /api/v1/preferences
    // (see services/backend-api/src/routes/versions/v1/preferences.ts). The body
    // is the flat `Partial<UserPreferences>` the settings organisms send, plus
    // an optional resolved `location` (lat/lon) that the backend persists as the
    // recommendation profile's home coordinates. Invalidates the current user so
    // `/auth/me` re-fetches the persisted values (the round-trip source of truth).
    updateUserPreferences: builder.mutation<UserPreferences, UpdateUserPreferencesArg>({
      query: (prefs) => ({
        url: 'v1/preferences',
        method: 'PATCH',
        body: prefs,
      }),
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    /**
     * Read the authenticated user's allowlisted notification toggles
     * (API-1489). Backend route: `GET /api/v1/users/me/notification-preferences`.
     * Defaults (all `true`) are applied server-side for toggles the user has
     * never set, so the response always contains all three keys.
     */
    getNotificationPreferences: builder.query<NotificationPreferencesResponse, void>({
      query: () => 'v1/users/me/notification-preferences',
      providesTags: [{ type: 'NotificationPreferences', id: 'SELF' }],
    }),

    /**
     * Replace the authenticated user's allowlisted notification toggles
     * (API-1489, WEB-437). Backend route:
     * `PUT /api/v1/users/me/notification-preferences`. The body is the strict
     * allowlist `{ savedEventReminders, newNearYou, priceDrops }`; unknown keys
     * are rejected with 400 INVALID_PAYLOAD server-side. Invalidates the
     * NotificationPreferences tag so the GET re-fetches the persisted value.
     */
    updateNotificationPreferences: builder.mutation<
      NotificationPreferencesResponse,
      UpdateNotificationPreferencesRequest
    >({
      query: (body) => ({
        url: 'v1/users/me/notification-preferences',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'NotificationPreferences', id: 'SELF' }],
    }),

    /**
     * Store (upsert) the caller's browser Web Push subscription (WEB-438,
     * backend API-1490). Backend route: `POST /api/v1/push/subscribe`. The body
     * is the browser `PushSubscription.toJSON()` shape; the owning user is taken
     * from the authenticated token, never the body. Re-POSTing the same
     * `endpoint` refreshes the keys rather than creating a duplicate row. Authed
     * via the shared `credentials: 'include'` base query — same path as every
     * other authed mutation here.
     */
    subscribeWebPush: builder.mutation<WebPushSubscriptionResponse, WebPushSubscriptionRequest>({
      query: (body) => ({
        url: 'v1/push/subscribe',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Remove the caller's browser Web Push subscription for a given `endpoint`
     * (WEB-438, backend API-1490). Backend route: `DELETE /api/v1/push/subscribe`.
     * Idempotent — a missing or foreign endpoint returns `{ deleted: 0 }` (200),
     * never an error, because the delete predicate is scoped to BOTH the endpoint
     * and the authenticated user id server-side.
     */
    unsubscribeWebPush: builder.mutation<WebPushUnsubscribeResponse, WebPushUnsubscribeRequest>({
      query: (body) => ({
        url: 'v1/push/subscribe',
        method: 'DELETE',
        body,
      }),
    }),

    /**
     * Cursor-paginated, newest-first list of the authenticated user's
     * notifications (WEB-432, backend API-1488). Backend route:
     * `GET /api/v1/notifications`. Provides the `Notifications` LIST tag so a
     * `markRead` mutation or a `userNotifications` WS frame refetches the list.
     */
    getNotifications: builder.query<NotificationListResponse, GetNotificationsArg | void>({
      query: (arg) => {
        const { cursor, limit } = arg ?? {};
        return {
          url: 'v1/notifications',
          params: {
            ...(cursor !== undefined ? { cursor } : {}),
            ...(limit !== undefined ? { limit } : {}),
          },
        };
      },
      providesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    /**
     * The authenticated user's unread-notification count (WEB-432, backend
     * API-1488). Backend route: `GET /api/v1/notifications/unread-count`.
     *
     * INVARIANT: this query is the ONLY authoritative source of the unread
     * count. The `userNotifications` WS frame invalidates the `Notifications`
     * tag (which this query provides) to trigger a refetch — it never carries
     * or mutates a client-side count.
     */
    getUnreadCount: builder.query<NotificationUnreadCountResponse, void>({
      query: () => 'v1/notifications/unread-count',
      providesTags: [{ type: 'Notifications', id: 'UNREAD_COUNT' }],
    }),

    /**
     * Mark notifications read (WEB-432, backend API-1488). Backend route:
     * `POST /api/v1/notifications/mark-read`. Body is either `{ all: true }`
     * or a non-empty `{ ids: [...] }` list; the user scope is enforced
     * server-side. Invalidates the `Notifications` tag so both the list and
     * the unread count refetch.
     */
    markRead: builder.mutation<MarkNotificationsReadResponse, MarkNotificationsReadRequest>({
      query: (body) => ({
        url: 'v1/notifications/mark-read',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Notifications', id: 'LIST' },
        { type: 'Notifications', id: 'UNREAD_COUNT' },
      ],
    }),

    /**
     * Complete the onboarding takeover (FRE-801). Persists the user's
     * selected category slugs and (optionally) a saved location/radius,
     * and stamps `User.preferences.onboardingCompletedAt` so the gate is
     * server-resolvable on subsequent visits.
     *
     * Backend route: POST /api/v1/preferences/onboarding (services/backend-api).
     */
    completeOnboarding: builder.mutation<CompleteOnboardingResponse, CompleteOnboardingRequest>({
      query: (body) => ({
        url: 'v1/preferences/onboarding',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    /**
     * Per-event saved/calendar flags for the authenticated user.
     * `GET /api/v1/events/:eventId/user-state`
     */
    getEventUserState: builder.query<EventUserStateResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `v1/events/${encodeURIComponent(eventId)}/user-state`,
      }),
      providesTags: (_result, _error, { eventId }) => [{ type: 'SavedEvents', id: eventId }],
    }),

    // Saved events — single source of truth for the dashboard carousel and
    // the /saved page. Cache tag invalidation keeps both views in sync across
    // save/unsave mutations.
    getSavedEvents: builder.query<SavedEventsApiResponse, GetSavedEventsArg>({
      query: ({ userId, limit, offset, withCoordinates }) => ({
        url: `v1/analytics/user/${encodeURIComponent(userId)}/saved`,
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
          ...(withCoordinates ? { withCoordinates: 'true' } : {}),
        },
      }),
      providesTags: (result) =>
        result?.savedEvents
          ? [
              ...result.savedEvents
                .map((entry) => entry.event?.id)
                .filter((id): id is string => typeof id === 'string' && id.length > 0)
                .map((id) => ({ type: 'SavedEvents' as const, id })),
              { type: 'SavedEvents' as const, id: 'LIST' },
            ]
          : [{ type: 'SavedEvents' as const, id: 'LIST' }],
    }),

    /**
     * Upcoming events saved by users the viewer follows (API-1736).
     * `GET /api/v1/users/me/following/saved`
     */
    getFollowingSavedEvents: builder.query<
      ResponseBody<paths, '/api/v1/users/me/following/saved', 'get', 200>,
      QueryParams<paths, '/api/v1/users/me/following/saved', 'get'>
    >({
      query: ({ limit, offset } = {}) => ({
        url: 'v1/users/me/following/saved',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
        },
      }),
      providesTags: [
        { type: 'User', id: 'SELF' },
        { type: 'SavedEvents', id: 'FOLLOWING' },
        { type: 'UserFollow', id: 'LIST' },
      ],
    }),

    // Distinct saved-venue count for the footer's "N places in your footprint"
    // line (WEB-477). Tagged under `SavedEvents/FOOTPRINT` so a save/unsave
    // refreshes it without a per-navigation refetch.
    getUserFootprint: builder.query<UserFootprintResponse, GetUserFootprintArg>({
      query: ({ userId }) => `v1/analytics/user/${encodeURIComponent(userId)}/footprint`,
      providesTags: [{ type: 'SavedEvents', id: 'FOOTPRINT' }],
    }),

    saveEvent: builder.mutation<{ success: boolean }, SaveEventArg>({
      query: ({ userId, eventId, sessionId }) => ({
        url: `v1/analytics/user/${encodeURIComponent(userId)}/save/${encodeURIComponent(eventId)}`,
        method: 'POST',
        body: {
          action: 'save',
          ...(sessionId !== undefined ? { sessionId } : {}),
        },
      }),
      // Optimistic update (FRE-751): only inject the new row on page-1 caches;
      // paginated pages past the first only bump `pagination.total` so counts
      // stay truthful without injecting an event that belongs on an earlier
      // window. Server invalidation still runs to reconcile ordering.
      async onQueryStarted({ userId, eventId }, { dispatch, queryFulfilled }) {
        const patches = patchSavedEventsForUser(dispatch, userId, (draft, args) => {
          const already = draft.savedEvents.some((entry) => entry.event?.id === eventId);
          if (already) return;
          const isFirstPage = (args.offset ?? 0) === 0;
          if (isFirstPage) {
            draft.savedEvents.unshift({
              savedAt: new Date().toISOString(),
              event: {
                id: eventId,
                title: '',
                date: new Date().toISOString(),
                categories: [],
                artists: [],
              },
            });
          }
          if (draft.pagination) {
            draft.pagination.total = (draft.pagination.total ?? 0) + 1;
          }
        });
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: 'SavedEvents', id: 'LIST' },
        { type: 'SavedEvents', id: eventId },
        // Refresh the footer footprint count (WEB-477).
        { type: 'SavedEvents', id: 'FOOTPRINT' },
      ],
    }),

    unsaveEvent: builder.mutation<{ success: boolean }, UnsaveEventArg>({
      query: ({ userId, eventId, sessionId }) => ({
        url: `v1/analytics/user/${encodeURIComponent(userId)}/save/${encodeURIComponent(eventId)}`,
        method: 'POST',
        body: {
          action: 'unsave',
          ...(sessionId !== undefined ? { sessionId } : {}),
        },
      }),
      // Optimistic update (FRE-751): only remove the matching entry from caches
      // that actually contain it; all other cache entries just decrement
      // `pagination.total`. This keeps paginated windows correct instead of
      // accidentally injecting shifts into later pages.
      async onQueryStarted({ userId, eventId }, { dispatch, queryFulfilled }) {
        const patches = patchSavedEventsForUser(dispatch, userId, (draft) => {
          const containedEvent = draft.savedEvents.some((entry) => entry.event?.id === eventId);
          if (containedEvent) {
            draft.savedEvents = draft.savedEvents.filter((entry) => entry.event?.id !== eventId);
          }
          if (draft.pagination) {
            draft.pagination.total = Math.max(0, (draft.pagination.total ?? 0) - 1);
          }
        });
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: 'SavedEvents', id: 'LIST' },
        { type: 'SavedEvents', id: eventId },
        // Refresh the footer footprint count (WEB-477).
        { type: 'SavedEvents', id: 'FOOTPRINT' },
      ],
    }),

    // RSVP ("I'm going") to an event (API-1485, WEB-412). Authenticated +
    // idempotent: re-RSVP'ing returns 200 with the unchanged state. The
    // optimistic button + count toggle lives in `EventDetailTemplate` (mirrors
    // the save flow); invalidating the `Event` tag re-fetches the authoritative
    // `goingCount` / `userIsGoing` on the next read.
    rsvpEvent: builder.mutation<EventRsvpResponse, RsvpEventArg>({
      query: ({ eventId }) => ({
        url: `v1/events/${encodeURIComponent(eventId)}/rsvp`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'Event', id: eventId }],
    }),

    // Remove an RSVP from an event (API-1485, WEB-412). Authenticated +
    // idempotent: deleting a non-existent RSVP returns 200.
    unrsvpEvent: builder.mutation<EventRsvpResponse, RsvpEventArg>({
      query: ({ eventId }) => ({
        url: `v1/events/${encodeURIComponent(eventId)}/rsvp`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'Event', id: eventId }],
    }),

    /**
     * Fetch Place Details for a single Google Place ID via the web
     * proxy. Cache key is the serialized `placeId`, so concurrent callers
     * with the same ID share one request (native RTK Query dedup) while
     * distinct IDs fetch in parallel. The proxy is pinned to the canonical
     * v1 FieldMask via `?v=card-v1`; malformed upstream payloads surface as
     * `INVALID_RESPONSE` through Zod validation rather than reaching the UI.
     *
     * TTL: `keepUnusedDataFor = PLACE_DETAILS_KEEP_UNUSED_SECONDS` (2 min) —
     * keeps "Open now" / hours fresh within a single browsing session
     * without re-hitting the upstream Places API on every selection. Photo
     * bytes are cached separately for 24 h via the photo proxy's
     * `Cache-Control` header. On search-sheet clear the entire RTK Query
     * cache is dropped via `lociApi.util.resetApiState()` so stale
     * place entries don't accumulate across repeated cycles.
     */
    getPlaceDetails: builder.query<PlaceCardDetailsV1, string>({
      queryFn: async (placeId) => {
        if (!isValidPlaceId(placeId)) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: 'Invalid place ID format',
              data: { code: 'INVALID_PLACE_ID' as const },
            },
          };
        }

        try {
          const url = `${PLACES_PROXY_PATH}/${encodeURIComponent(placeId)}?v=${PLACE_DETAILS_VERSION.CARD_V1}`;
          const correlationId =
            globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
          const response = await fetch(url, {
            credentials: 'include',
            headers: { 'x-correlation-id': correlationId },
          });

          if (!response.ok) {
            const body = await response.text().catch(() => '');
            return {
              error: {
                status: response.status,
                data: body,
              },
            };
          }

          const raw: unknown = await response.json();
          const parsed = placeCardDetailsV1Schema.safeParse(raw);
          if (!parsed.success) {
            return {
              error: {
                status: 'CUSTOM_ERROR' as const,
                error: 'Invalid response from Places proxy',
                data: {
                  code: 'INVALID_RESPONSE' as const,
                  issues: parsed.error.issues,
                },
              },
            };
          }

          return { data: parsed.data };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR' as const,
              error: error instanceof Error ? error.message : 'Unknown fetch error',
            },
          };
        }
      },
      keepUnusedDataFor: PLACE_DETAILS_KEEP_UNUSED_SECONDS,
      providesTags: (_result, _error, placeId) => [{ type: 'Place', id: placeId }],
    }),

    // Per-artist follow state (WEB-923). Hydrates FollowArtistButton without
    // depending on the followed-artists LIST page (limit: 100 miss).
    // Backend: GET /api/v1/artists/:id/follow -> { artistId, following }
    getArtistFollow: builder.query<ArtistFollowState, { artistId: string }>({
      query: ({ artistId }) => ({
        url: `v1/artists/${encodeURIComponent(artistId)}/follow`,
      }),
      providesTags: (_result, _error, { artistId }) => [{ type: 'ArtistFollow', id: artistId }],
    }),

    // Artist follow mutations (FRE-755). The connected LineupSection owns
    // per-row optimistic state locally and rolls it back on error; the
    // `ArtistFollow` per-id tag invalidation refreshes `getArtistFollow`
    // (WEB-923) plus the followed-artists LIST rail.
    // Backend contract:
    //   POST   /api/v1/artists/:id/follow  -> { artistId, following: true }
    //   DELETE /api/v1/artists/:id/follow  -> { artistId, following: false }
    followArtist: builder.mutation<ArtistFollowState, { artistId: string }>({
      query: ({ artistId }) => ({
        url: `v1/artists/${encodeURIComponent(artistId)}/follow`,
        method: 'POST',
      }),
      // FRE-925 — also invalidate the followed-artists LIST so the Discover
      // "Artists you follow" rail refetches after a (un)follow.
      invalidatesTags: (_result, _error, { artistId }) => [
        { type: 'ArtistFollow', id: artistId },
        { type: 'ArtistFollow', id: 'LIST' },
      ],
    }),

    unfollowArtist: builder.mutation<ArtistFollowState, { artistId: string }>({
      query: ({ artistId }) => ({
        url: `v1/artists/${encodeURIComponent(artistId)}/follow`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { artistId }) => [
        { type: 'ArtistFollow', id: artistId },
        { type: 'ArtistFollow', id: 'LIST' },
      ],
    }),

    // Venue follow mutations (FRE-925). Mirror the artist mutations:
    //   POST   /api/v1/venues/:id/follow  -> { venueId, following: true }
    //   DELETE /api/v1/venues/:id/follow  -> { venueId, following: false }
    // Both invalidate the per-venue tag and the followed-venues LIST so the
    // Discover "Venues you follow" rail refetches.
    followVenue: builder.mutation<VenueFollowState, { venueId: string }>({
      query: ({ venueId }) => ({
        url: `v1/venues/${encodeURIComponent(venueId)}/follow`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { venueId }) => [
        { type: 'VenueFollow', id: venueId },
        { type: 'VenueFollow', id: 'LIST' },
      ],
    }),

    unfollowVenue: builder.mutation<VenueFollowState, { venueId: string }>({
      query: ({ venueId }) => ({
        url: `v1/venues/${encodeURIComponent(venueId)}/follow`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { venueId }) => [
        { type: 'VenueFollow', id: venueId },
        { type: 'VenueFollow', id: 'LIST' },
      ],
    }),

    // Followed-entity rails for the Discover surface (FRE-925).
    //   GET /api/v1/users/me/followed-artists -> { artists, pagination }
    //   GET /api/v1/users/me/followed-venues  -> { venues,  pagination }
    getFollowedArtists: builder.query<
      FollowedArtistsResponse,
      { limit?: number; offset?: number } | void
    >({
      query: (arg) => ({
        url: 'v1/users/me/followed-artists',
        params: {
          ...(arg?.limit !== undefined ? { limit: arg.limit } : {}),
          ...(arg?.offset !== undefined ? { offset: arg.offset } : {}),
        },
      }),
      providesTags: [{ type: 'ArtistFollow', id: 'LIST' }],
    }),

    getFollowedVenues: builder.query<
      FollowedVenuesResponse,
      { limit?: number; offset?: number } | void
    >({
      query: (arg) => ({
        url: 'v1/users/me/followed-venues',
        params: {
          ...(arg?.limit !== undefined ? { limit: arg.limit } : {}),
          ...(arg?.offset !== undefined ? { offset: arg.offset } : {}),
        },
      }),
      providesTags: [{ type: 'VenueFollow', id: 'LIST' }],
    }),

    // Batch mark-seen — zeroes every venue's "new since last visit" count.
    //   POST /api/v1/users/me/followed-venues/seen -> { markedCount, seenAt }
    markVenuesSeen: builder.mutation<MarkSeenResponse, void>({
      query: () => ({
        url: 'v1/users/me/followed-venues/seen',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'VenueFollow', id: 'LIST' }],
    }),

    // Community notes for an event (FRE-755). Public read; the UI renders an
    // empty-state placeholder when the array is empty.
    getEventNotes: builder.query<EventNotesResponse, string>({
      query: (eventId) => `v1/events/${encodeURIComponent(eventId)}/notes`,
      providesTags: (_result, _error, eventId) => [{ type: 'EventNotes', id: eventId }],
    }),

    /**
     * Event-social affinity payload (friends-going, taste-fit, crowd insight).
     * `GET /api/v1/events/:eventId/social` — authenticated-only fields are
     * empty/null for anonymous requests (server-gated, honest absence).
     * Powers the map place-detail match-strip's friends-going row (FRE-1118).
     */
    getEventSocial: builder.query<EventSocialResponse, { eventId: string }>({
      query: ({ eventId }) => `v1/events/${encodeURIComponent(eventId)}/social`,
      // Shares the `Event`/`eventId` tag with `rsvpEvent`/`unrsvpEvent`/
      // `saveEvent`/`unsaveEvent` so this stays consistent with every other
      // event-scoped query in this file, even though a friend's own
      // RSVP/save (the thing that actually changes "who's going") happens
      // in a different session this client can't observe directly.
      providesTags: (_result, _error, { eventId }) => [{ type: 'Event', id: eventId }],
    }),

    // Social-graph surfaces for the Profile screen (FRE-777).
    //   GET /api/v1/users/me/following  -> { following, pagination }
    //   GET /api/v1/users/me/tickets    -> { tickets,   pagination }
    // The UI renders empty-state tombstones when the arrays are empty.
    getUserFollowing: builder.query<UserFollowingResponse, { limit?: number; offset?: number }>({
      query: ({ limit, offset } = {}) => ({
        url: 'v1/users/me/following',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
        },
      }),
      providesTags: [{ type: 'UserFollow', id: 'LIST' }],
    }),

    getUserTickets: builder.query<
      UserTicketsResponse,
      { limit?: number; offset?: number; sort?: string }
    >({
      query: ({ limit, offset, sort } = {}) => ({
        url: 'v1/users/me/tickets',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
          ...(sort !== undefined ? { sort } : {}),
        },
      }),
      providesTags: [{ type: 'Ticket', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetTrendingEventsQuery,
  useUpdateUserPreferencesMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useSubscribeWebPushMutation,
  useUnsubscribeWebPushMutation,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkReadMutation,
  useCompleteOnboardingMutation,
  useGetCurrentUserQuery,
  useListAuthSessionsQuery,
  useGetCsrfTokenQuery,
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useLogoutAllMutation,
  useDeleteAccountMutation,
  useExportAccountDataMutation,
  useGetBrowsingHistorySummaryQuery,
  useLazyGetBrowsingHistorySummaryQuery,
  useClearBrowsingHistoryMutation,
  useClearStoredLocationMutation,
  useRevokeSessionMutation,
  useRefreshTokenMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useResendVerificationMutation,
  useRequestMagicLinkMutation,
  useConsumeMagicLinkMutation,
  useMapChatMutation,
  useChangePasswordMutation,
  useChangeEmailMutation,
  useUpdateProfileMutation,
  usePasskeyAuthenticationBeginMutation,
  usePasskeyAuthenticationFinishMutation,
  useUnlinkProviderMutation,
  useUnlinkPasskeyMutation,
  useEnableMagicLinkSignInMutation,
  usePasskeyRegisterBeginMutation,
  usePasskeyRegisterFinishMutation,
  useGetSearchSuggestionsQuery,
  useGetEventsListQuery,
  useGetCategoriesQuery,
  useGetForYouRecommendationsQuery,
  useGetHappeningNowRecommendationsQuery,
  useGetNewArtistRecommendationsQuery,
  useGetSimilarEventsQuery,
  useRecordRecommendationFeedbackMutation,
  useGetSavedEventsQuery,
  useLazyGetSavedEventsQuery,
  useGetFollowingSavedEventsQuery,
  useGetUserFootprintQuery,
  useGetEventUserStateQuery,
  useSaveEventMutation,
  useUnsaveEventMutation,
  useRsvpEventMutation,
  useUnrsvpEventMutation,
  useGetPlaceDetailsQuery,
  useLazyGetPlaceDetailsQuery,
  useGetArtistFollowQuery,
  useFollowArtistMutation,
  useUnfollowArtistMutation,
  useFollowVenueMutation,
  useUnfollowVenueMutation,
  useGetFollowedArtistsQuery,
  useGetFollowedVenuesQuery,
  useMarkVenuesSeenMutation,
  useGetEventNotesQuery,
  useGetUserFollowingQuery,
  useGetUserTicketsQuery,
  useGetEventSocialQuery,
} = lociApi;

/**
 * Navigate the browser to the OAuth provider link flow.
 * This must be a full-page navigation (not fetch/XHR) because the backend
 * responds with a 302 redirect to the provider's authorization URL.
 */
export function navigateToProviderLink(provider: OAuthProviderName, redirectUrl?: string): void {
  if (typeof window === 'undefined') return;
  const base = getApiBaseUrl();
  const params = redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : '';
  window.location.href = `${base}/api/v1/auth/oauth/${provider}/link${params}`;
}
